import { useCallback, useEffect, useRef, useState } from "react";
import { askAmazonCopilot } from "../../api/client";
import type { AmzAiFinding, AmzAiProposal, AmzAttributes, AmzFieldDef } from "../../types/amazon";
import { labelForKey } from "../../lib/amazonContent";
import { Badge, Spinner } from "./AmzUi";
import { SEVERITY_TONE, errorMessage } from "./helpers";

export interface CopilotCommand {
  nonce: number;
  text: string;
  /** null = ページ全体 */
  target: string | null;
}

type ProposalStatus = "pending" | "applied" | "canceled";

type Msg =
  | { id: string; type: "user"; text: string }
  | { id: string; type: "ai"; text: string }
  | { id: string; type: "review"; findings: AmzAiFinding[]; brandNote: string; propIds: string[] }
  | { id: string; type: "proposal"; p: AmzAiProposal; status: ProposalStatus };

interface CopilotPanelProps {
  /** false の間は非表示（メッセージ履歴は保持する） */
  open: boolean;
  sku: string;
  productName: string;
  fields: AmzFieldDef[];
  draft: AmzAttributes;
  target: string | null;
  onClearTarget: () => void;
  onClose: () => void;
  command: CopilotCommand | null;
  /** 提案を下書きへ適用（source=AI）。複数まとめて渡せる。 */
  onApply: (items: { field: string; newValue: string }[]) => void;
}

const INTRO: Msg = {
  id: "m0",
  type: "ai",
  text: "商品ページの編集をお手伝いします。各項目の「AI」ボタンで対象を選ぶか、ページ全体への指示を入力してください。",
};

const QUICK_TARGET = ["検索キーワードを意識して改善", "もっとシンプルに", "STOQらしい高級感のある表現に", "重複表現をなくして"];
const QUICK_ALL = [
  "商品ページ全体をチェックして",
  "真空保存と耐熱性のメリットが十分伝わっているかチェックして",
  "説明の重複を削除",
  "50代女性にも分かりやすく",
];

let msgSeq = 1;
const mid = () => `m${msgSeq++}`;

export function CopilotPanel({ open, sku, productName, fields, draft, target, onClearTarget, onClose, command, onApply }: CopilotPanelProps) {
  const [msgs, setMsgs] = useState<Msg[]>([INTRO]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const handledNonce = useRef<number | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, loading]);

  const ask = useCallback(
    async (text: string, tgt: string | null) => {
      const t = text.trim();
      if (!t) return;
      setMsgs((m) => [...m, { id: mid(), type: "user", text: t }]);
      setInput("");
      setLoading(true);
      // README: 対象なし、または「レビュー/全体/チェック」を含む指示はページ全体として扱う
      const effective = !tgt || /レビュー|全体|チェック/.test(t) ? null : tgt;
      try {
        const res = await askAmazonCopilot(sku, { target: effective, instruction: t, draft: draftRef.current });
        const props: Msg[] = res.proposals.map((p) => ({ id: mid(), type: "proposal", p, status: "pending" }));
        const next: Msg[] = [{ id: mid(), type: "ai", text: res.message }];
        if (res.findings.length > 0) {
          next.push({ id: mid(), type: "review", findings: res.findings, brandNote: res.brandNote, propIds: props.map((x) => x.id) });
        }
        setMsgs((m) => [...m, ...next, ...props]);
      } catch (e) {
        setMsgs((m) => [...m, { id: mid(), type: "ai", text: `AIの呼び出しに失敗しました: ${errorMessage(e)}` }]);
      } finally {
        setLoading(false);
      }
    },
    [sku]
  );

  // 外部（「AIで商品ページをレビュー」ボタン）からの指示
  useEffect(() => {
    if (!command || handledNonce.current === command.nonce) return;
    handledNonce.current = command.nonce;
    void ask(command.text, command.target);
  }, [command, ask]);

  const setStatus = (id: string, status: ProposalStatus) =>
    setMsgs((m) => m.map((x) => (x.type === "proposal" && x.id === id ? { ...x, status } : x)));

  const apply = (id: string, p: AmzAiProposal) => {
    onApply([{ field: p.field, newValue: p.newValue }]);
    setStatus(id, "applied");
  };

  const applyAll = (propIds: string[]) => {
    const pending = msgs.filter((x): x is Extract<Msg, { type: "proposal" }> => x.type === "proposal" && propIds.includes(x.id) && x.status === "pending");
    if (pending.length === 0) return;
    onApply(pending.map((x) => ({ field: x.p.field, newValue: x.p.newValue })));
    setMsgs((m) => m.map((x) => (x.type === "proposal" && pending.some((p) => p.id === x.id) ? { ...x, status: "applied" } : x)));
  };

  const regen = async (id: string, p: AmzAiProposal) => {
    setLoading(true);
    try {
      const res = await askAmazonCopilot(sku, { target: p.field, instruction: "別の表現で、もう一度提案して", draft: draftRef.current });
      const np = res.proposals[0];
      if (np) setMsgs((m) => m.map((x) => (x.type === "proposal" && x.id === id ? { ...x, p: np, status: "pending" } : x)));
      else setMsgs((m) => [...m, { id: mid(), type: "ai", text: res.message }]);
    } catch (e) {
      setMsgs((m) => [...m, { id: mid(), type: "ai", text: `再生成に失敗しました: ${errorMessage(e)}` }]);
    } finally {
      setLoading(false);
    }
  };

  const quick = target ? QUICK_TARGET : QUICK_ALL;

  return (
    <aside id="amz-ai-panel" className="amz-ai" aria-label="AI Copilot" style={open ? undefined : { display: "none" }}>
      <div className="amz-ai-head">
        <div className="amz-ai-head-top">
          <b>AI Copilot</b>
          <button type="button" className="amz-ai-close" onClick={onClose} aria-label="AIパネルを閉じる">
            ×
          </button>
        </div>
        <div className="amz-ai-target">
          <span className="muted">現在の対象</span>
          <span className="name">{productName}</span>
          <div className="amz-ai-target-row">
            <span className="label">{target ? labelForKey(target, fields) : "商品ページ全体"}</span>
            {target && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onClearTarget();
                }}
              >
                ページ全体に戻す
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="amz-ai-msgs" ref={scrollRef}>
        {msgs.map((m) => {
          if (m.type === "user") return <div key={m.id} className="amz-msg-user">{m.text}</div>;
          if (m.type === "ai") return <div key={m.id} className="amz-msg-ai">{m.text}</div>;
          if (m.type === "review") {
            const pend = msgs.filter((x) => x.type === "proposal" && m.propIds.includes(x.id) && x.status === "pending").length;
            return (
              <div key={m.id} className="amz-msg-card">
                <span className="amz-msg-card-title">レビュー結果</span>
                {m.findings.map((f, i) => (
                  <div key={i} className="amz-finding">
                    <Badge tone={SEVERITY_TONE[f.level]} size="xs" mono>
                      {f.level}
                    </Badge>
                    <span>
                      <b>{f.item}</b>&emsp;{f.text}
                    </span>
                  </div>
                ))}
                <div className="amz-brand-note">{m.brandNote}</div>
                <div className="amz-msg-card-foot">
                  <span>修正案 {m.propIds.length}件</span>
                  <button type="button" className="amz-btn primary xs" onClick={() => applyAll(m.propIds)} disabled={pend === 0}>
                    すべて適用
                  </button>
                </div>
              </div>
            );
          }
          const { p, status } = m;
          return (
            <div key={m.id} className={`amz-msg-card ${status === "applied" ? "applied" : ""}`}>
              <div className="amz-prop-head">
                <span>{p.label}</span>
                <span className="amz-prop-meta">
                  <Badge tone={p.risk === "低" ? "success" : p.risk === "中" ? "warning" : "error"} size="xs">
                    リスク {p.risk}
                  </Badge>
                  信頼度 {p.confidence}%
                </span>
              </div>
              <div className="amz-diff-old">
                <b>変更前</b>&emsp;{p.oldValue || "（未設定）"}
              </div>
              <div className="amz-diff-new">
                <b>変更案</b>&emsp;{p.newValue}
              </div>
              <span className="amz-prop-reason">{p.reason}</span>
              {status === "pending" ? (
                <div className="amz-btn-row" style={{ gap: 6 }}>
                  <button type="button" className="amz-btn xs" onClick={() => setStatus(m.id, "canceled")}>
                    キャンセル
                  </button>
                  <button type="button" className="amz-btn xs" onClick={() => void regen(m.id, p)} disabled={loading}>
                    再生成
                  </button>
                  <button type="button" className="amz-btn primary xs" onClick={() => apply(m.id, p)}>
                    適用
                  </button>
                </div>
              ) : (
                <span className="amz-prop-done" style={{ color: status === "applied" ? "var(--amz-ok)" : "var(--amz-muted)" }}>
                  {status === "applied" ? "下書きに適用済み" : "キャンセルしました"}
                </span>
              )}
            </div>
          );
        })}
        {loading && (
          <div className="amz-loading-line" style={{ fontSize: 12 }}>
            <Spinner size="sm" />
            生成中…
          </div>
        )}
      </div>
      <div className="amz-ai-foot">
        <div className="amz-quick">
          {quick.map((label) => (
            <button key={label} type="button" disabled={loading} onClick={() => void ask(label, target)}>
              {label}
            </button>
          ))}
        </div>
        <div className="amz-ai-input">
          <textarea
            rows={2}
            value={input}
            placeholder="AIへの指示を入力（Enterで送信）"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // 日本語入力の変換確定のEnterでは送信しない
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (!loading) void ask(input, target);
              }
            }}
          />
          <button type="button" disabled={loading || !input.trim()} onClick={() => void ask(input, target)}>
            送信
          </button>
        </div>
        <span className="amz-ai-note">AIの提案は下書きにのみ適用され、Amazonへは自動送信しません。</span>
      </div>
    </aside>
  );
}
